"use client"

// imports
;!function(){try{var e="undefined"!=typeof window?window:"undefined"!=typeof global?global:"undefined"!=typeof globalThis?globalThis:"undefined"!=typeof self?self:{};e._sentryModuleMetadata=e._sentryModuleMetadata||{},e._sentryModuleMetadata[(new e.Error).stack]=Object.assign({},e._sentryModuleMetadata[(new e.Error).stack],{"_sentryBundlerPluginAppKey:dotorg-next":true});}catch(e){}}();import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import Link from 'next/link';
import { KeyboardEvent, Ref } from 'react';

dayjs.extend(utc);

// lib files
import { SHOW_BADGE_FLAG_CONFIG } from '@/lib/constants';
import { Franchise } from '@/lib/types/api/franchise-data';
import { ShowNode } from '@/lib/types/graphql/searchShowsResponse';
import { ShowRowContent, ShowDetails, ShowOrFranchiseEnum } from '@/lib/types/api/show-data';

// types
import type { ShowFlags } from '@/lib/types/api/show-data';

// components
import Badge from '@/components/Badge/Badge';
import ITSImage from '@/components/ITSImage/ITSImage';

// svg's
import CompassRoseCorner from '@/public/svg/compass-rose-corner.svg';

// styles
import styles from './ShowPoster.module.scss';

export type ShowWithImages = ShowDetails | ShowRowContent
export interface ShowPosterType {
  images: {
    'show-poster2x3': string;
  };
  slug: string;
  title: string;
  item_type: ShowOrFranchiseEnum;
}
export interface FranchisePosterType {
  images: {
    'franchise-poster2x3': string;
  };
  slug: string;
  title: string;
  item_type: ShowOrFranchiseEnum;
}

export interface ShowPosterProps {
  className?: string;
  hideShowBadges?: boolean;
  href?: string;
  index?: number;
  onClick?: (show: ShowRowContent, index?: number, linkHref?: string) => void;
  onKeyDown?: (e: KeyboardEvent<HTMLAnchorElement>) => void;
  onFocus?: () => void;
  ref?: Ref<HTMLAnchorElement>;
  show: ShowWithImages | ShowNode | Franchise | ShowPosterType | FranchisePosterType;
  showMyStationBadge?: boolean;
  sizes?: string;
  width?: number;
}

const getShowBadgeFlag = (
  show: ShowPosterProps['show'],
  showMyStationBadge: boolean,
): keyof ShowFlags | undefined => {
  if (!('flags' in show) || !show.flags) return undefined;

  const flags = show.flags as ShowFlags;

  // Badge priority order: coming_soon > is_new > new_season > new_episode > my_station
  // passport_only is independent from the rest of the badges
  const badgePriorityOrder: Array<keyof ShowFlags> = [
    'coming_soon',
    'is_new',
    'new_season',
    'new_episode',
    'my_station',
  ];

  const isBadgeEnabled = (key: keyof ShowFlags) => key !== 'my_station' || showMyStationBadge;

  return badgePriorityOrder.find((key) => flags[key] && isBadgeEnabled(key));
};

const ShowPoster = (props: ShowPosterProps) => {
  const {
    className,
    hideShowBadges = false,
    href,
    index,
    onClick,
    onFocus,
    onKeyDown,
    ref,
    show,
    showMyStationBadge = false,
    sizes,
    width = 227,
  } = props;

  const { item_type } = show;

  let imgSrc = null;
  let parentSlug = 'show';

  switch (item_type?.toLowerCase()) {
    case ShowOrFranchiseEnum.Franchise:
      const franchise = show as Franchise;
      imgSrc = franchise.images?.['franchise-poster2x3'] || franchise.image;
      parentSlug = 'franchise';
      break;
    case ShowOrFranchiseEnum.Show:
      // sorry for all of this - typescript is a right bastard
      if(Object.hasOwn(show, 'images')) {
        const showWithImages = show as ShowWithImages

        if (showWithImages.images?.['show-poster2x3']) {
          imgSrc = showWithImages.images['show-poster2x3'];
        }
      } else if(Object.hasOwn(show, 'image')) {
        const showWithImage = show as ShowNode
        imgSrc = showWithImage.image as string;
      }
  }

  const linkHref = href || `/${parentSlug}/${show.slug}/`;

  const handleClick = () => {
    // We need to expressly check for undefined because 0 is considered falsey
    if (index !== undefined && onClick) {
      onClick(show as ShowRowContent, index, linkHref);
    } else if (onClick) {
      onClick(show as ShowRowContent, undefined, linkHref);
    }
  };

  const showBadgeFlag = hideShowBadges ? undefined : getShowBadgeFlag(show, showMyStationBadge);
  const badgeConfig = showBadgeFlag ? SHOW_BADGE_FLAG_CONFIG[showBadgeFlag] : undefined;

  // Check if show has passport_only flag
  const isPassportOnly =
    !hideShowBadges &&
    'flags' in show &&
    show.flags?.passport_only === true;

  let badgeText = badgeConfig?.text;

  // Handle "coming_soon" badge with premiere date
  if (showBadgeFlag === 'coming_soon' && 'premiere_date' in show && show.premiere_date) {
    const premiereDate = dayjs.utc(show.premiere_date);
    if (premiereDate.isValid()) {
      badgeText = `Coming ${premiereDate.format('MMM D')}`;
    }
  }

  const badgeColor = badgeConfig?.color;

  let classNames = styles.show_poster__link;

  if (className) {
    classNames += ` ${className}`;
  }

  const showPosterSizes = sizes ? sizes : `
    (min-width: 1700px) 211px,
    (min-width: 1440px) calc((100vw - 240px) / 7),
    (min-width: 1024px) calc((100vw - 224px) / 6),
    (min-width: 768px) calc((100vw - 104px) / 5),
    (min-width: 600px) calc((100vw - 56px) / 4),
    calc((100vw - 48px) / 3)
    `;

  return (
    <Link
      href={linkHref}
      className={classNames}
      onClick={handleClick}
      ref={ref}
      onKeyDown={onKeyDown}
      onFocus={onFocus}
      data-sentry-element="Link" data-sentry-component="ShowPoster" data-sentry-source-file="ShowPoster.tsx">
      { isPassportOnly && (
        <span className={styles.show_poster__compass_rose}>
          <CompassRoseCorner aria-label="Available with Passport"/>
        </span>
      )}
      {imgSrc ? (
        <ITSImage
          src={imgSrc}
          alt={show.title}
          width={width}
          height={Math.round(width * 1.5)}
          resizeWithCrop={true}
          srcSetSizes={[[86,129],[178,268], [249,374], [374,561],[498,748]]}
          sizes={showPosterSizes}
        />
      ) : (
        <p className={styles.show_poster__fallback}>{show.title}</p>
      )}
      { badgeText && (
        <Badge
          className={styles.show_poster__badge}
          style={badgeColor}
        >
          {badgeText}
        </Badge>
      )}
    </Link>
  );
};

export default ShowPoster;
